import { Button, CircularProgress, Container, Divider, Grid, Typography, Box, Paper, IconButton, Avatar, Stack } from "@mui/material";
import { Close, Badge as BadgeIcon, Email, Phone, Transgender, ManageAccounts, CalendarMonth } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import moment from "moment";

// Users Details View Component
const UsersDetail = ({ handleViewModalClose, userId }) => {
    // SNACKBAR
    const { enqueueSnackbar } = useSnackbar();
    const axiosPrivate = useAxiosPrivate();

    // QUERY
    const { isLoading, data, isFetching, error } = useQuery(["user_detail", userId], () => {
        return axiosPrivate.get(`/users/${userId}`);
    }, { refetchOnWindowFocus: false });

    // USEFFECTS
    useEffect(() => {
        if (error) {
            enqueueSnackbar(error?.response?.data?.error?.message || error?.message || "Network Error!", {
                variant: "error",
                autoHideDuration: 2000
            });
        }
    }, [enqueueSnackbar, error]);

    const user = data?.data?.data;

    // RENDER
    return (
        <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: '440px' },
                    borderRadius: { xs: '24px 24px 0 0', sm: 6 },
                    boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.15)",
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                <IconButton
                    onClick={handleViewModalClose}
                    sx={{ position: 'absolute', right: 16, top: 16, color: 'text.disabled' }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                bgcolor: 'primary.main',
                                fontSize: '32px',
                                fontWeight: 800,
                                boxShadow: '0 8px 20px rgba(255,170,0,0.25)',
                                mb: 2
                            }}
                        >
                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: 'secondary.main', letterSpacing: '-0.5px' }}>
                            {user?.first_name} {user?.last_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {user?.role?.name || 'Basic User'}
                        </Typography>
                    </Box>

                    {isLoading || isFetching ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={32} color="primary" />
                        </Box>
                    ) : (
                        <Stack spacing={2.5}>
                            <DetailItem icon={<BadgeIcon sx={{ color: 'primary.main', fontSize: 20 }} />} label="Full Name" value={`${user?.first_name} ${user?.middle_name} ${user?.last_name}`} />
                            <DetailItem icon={<Email sx={{ color: 'primary.main', fontSize: 20 }} />} label="Email Address" value={user?.email} />
                            <DetailItem icon={<Phone sx={{ color: 'primary.main', fontSize: 20 }} />} label="Phone Number" value={user?.phone} />
                            <DetailItem icon={<Transgender sx={{ color: 'primary.main', fontSize: 20 }} />} label="Gender" value={user?.gender} />
                            <DetailItem icon={<CalendarMonth sx={{ color: 'primary.main', fontSize: 20 }} />} label="Joined on" value={moment(user?.created_at).format("MMMM Do, YYYY")} />
                        </Stack>
                    )}

                    <Box sx={{ mt: 4 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{
                                borderRadius: 3.5,
                                textTransform: "none",
                                fontWeight: 700,
                                py: 1.2,
                                boxShadow: '0 4px 12px rgba(255,170,0,0.2)',
                                bgcolor: 'primary.main',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }}
                            onClick={handleViewModalClose}
                        >
                            Done
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Grid>
    );
};

function DetailItem({ icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', display: 'flex' }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                    {value || 'N/A'}
                </Typography>
            </Box>
        </Box>
    );
}

export default UsersDetail;